import React, { useState, useEffect, useCallback } from 'react';
import { AddHandlingTierProps } from 'src/features/types/r2fTypes';
import { WarehouseHandlingTier } from 'src/types/WarehouseHandlingTier';
import { 
    registerAddedTier, 
    isTierAdded, 
    getAddedTiers, 
    logTierRegistry, 
    TIER_ADDED_EVENT 
} from 'src/features/components/hierarchicalTable/tierRegistry';

/**
 * Component for adding a new handling tier to a production capability
 * Supports a compact style for use in actions column
 */
export const AddR2FNode: React.FC<AddHandlingTierProps> = ({
    productionCapabilityId,
    onAdd,
    userCanEdit,
    initialAdding = false,
    existingHandlingTiers = [],  // This will be populated by the parent component
    buttonStyle = 'default' // Default to standard button style
}) => {
    // State for showing the add form
    const [isAdding, setIsAdding] = useState(initialAdding);
    const [selectedTier, setSelectedTier] = useState<string>('');
    const [availableTiers, setAvailableTiers] = useState<string[]>([]);
    const [registryUpdateCounter, setRegistryUpdateCounter] = useState(0);
    
    // Listen for registry update events
    useEffect(() => {
        console.warn(`[R2F-NODE] Setting up tier-registry-update event listener for productionCapability ${productionCapabilityId}`);
        
        const handleTierAdded = (event: Event) => {
            // Force re-render when any tier is added
            console.warn(`[R2F-NODE] Received tier-registry-update event, forcing re-render`);
            setRegistryUpdateCounter(prev => prev + 1);
        };
        
        // Add event listener
        window.addEventListener(TIER_ADDED_EVENT, handleTierAdded);
        
        // Clean up
        return () => {
            window.removeEventListener(TIER_ADDED_EVENT, handleTierAdded);
        };
    }, [productionCapabilityId]);
    
    // Filter handling tiers to exclude ones that already exist
    useEffect(() => {
        console.warn(`[R2F-NODE] Filtering available tiers for productionCapability ${productionCapabilityId}, registryUpdateCounter=${registryUpdateCounter}`);
        
        const allTiers = Object.values(WarehouseHandlingTier);
        
        // Filter out existing tiers AND tiers that have been added to the registry
        const filteredTiers = allTiers.filter(tier => {
            // Check if tier exists in existingNodeIds
            const isExisting = existingHandlingTiers.includes(tier);
            
            // Also check the registry to see if it's been added but not reflected in existingNodeIds yet
            const isInRegistry = isTierAdded(productionCapabilityId, tier);
            
            if (isExisting) {
                console.warn(`[R2F-NODE] Filtering out existing tier: ${tier}`);
            }
            
            if (isInRegistry && !isExisting) {
                console.warn(`[R2F-NODE] Filtering out tier from registry: ${tier}`);
            }
            
            return !isExisting && !isInRegistry;
        });
        
        console.warn(`[R2F-NODE] Final available tiers: ${filteredTiers.join(', ')}`);
        setAvailableTiers(filteredTiers);
    }, [existingHandlingTiers, productionCapabilityId, registryUpdateCounter]);
    
    // Handle showing the add form
    const handleShowAddForm = useCallback(() => {
        setIsAdding(true);
    }, []);
    
    // Handle canceling the add form
    const handleCancelAdd = useCallback(() => {
        setIsAdding(false);
        setSelectedTier('');
    }, []);
    
    // Handle adding the handling tier
    const handleAddTier = useCallback(() => {
        if (selectedTier) {
            console.warn(`🚨 [R2F-NODE] Adding tier ${selectedTier} for productionCapability ${productionCapabilityId}`);
            
            try {
                // Debug what we're about to do
                console.warn(`🚨 [R2F-NODE] Adding tier ${selectedTier} for productionCapabilityId ${productionCapabilityId}`);
                
                // First register with the tier registry so immediate UI updates can happen
                registerAddedTier(productionCapabilityId, selectedTier);
                console.warn(`1️⃣ [R2F-NODE] Added to registry: productionCapabilityId=${productionCapabilityId}, tier=${selectedTier}`);
                
                // Call parent component's add handler AFTER registry update
                // This sequence is important - we want the tier to be registered before the UI updates
                const result = onAdd(productionCapabilityId, selectedTier);
                console.warn(`2️⃣ [R2F-NODE] Called onAdd with productionCapabilityId=${productionCapabilityId}, tier=${selectedTier}, result:`, result);
                
                // Force a re-render of the dropdown immediately 
                setRegistryUpdateCounter(prev => prev + 1);
                
                // Reset component state
                setIsAdding(false);
                setSelectedTier('');
                
                // Recheck available tiers after a small delay to ensure UI is updated
                setTimeout(() => {
                    console.warn(`3️⃣ [R2F-NODE] Refreshing available tiers after adding ${selectedTier}`);
                    setRegistryUpdateCounter(prev => prev + 1);
                }, 100);
            } catch (e) {
                console.error("[R2F-NODE] Error processing add tier action:", e);
                // Even if registration fails, we still want to reset the UI
                setIsAdding(false);
                setSelectedTier('');
            }
        }
    }, [onAdd, selectedTier, productionCapabilityId]);
    
    // Check if the add button should be disabled
    const isAddDisabled = !selectedTier || availableTiers.length === 0;
    
    if (!userCanEdit) {
        return null;
    }
    
    if (isAdding) {
        if (availableTiers.length === 0) {
            return (
                <div className="no-tiers-available">
                    All handling tiers already exist for this production capability.
                    <button 
                        className="cancel-button"
                        onClick={handleCancelAdd}
                    >Cancel</button>
                </div>
            );
        }
        
        return (
            <div className="add-tier-container">
                <select
                    value={selectedTier}
                    onChange={e => setSelectedTier(e.target.value)}
                    className="handling-tier-select"
                    autoFocus
                >
                    <option value="">Select a handling tier</option>
                    {availableTiers.map(tier => (
                        <option key={tier} value={tier}>{tier}</option>
                    ))}
                </select>
                <div className="add-tier-actions">
                    <button 
                        className="save-button"
                        onClick={handleAddTier}
                        disabled={isAddDisabled}
                    >Add</button>
                    <button 
                        className="cancel-button"
                        onClick={handleCancelAdd}
                    >Cancel</button>
                </div>
            </div>
        );
    }
    
    // Only show the button if there are tiers available to add
    if (buttonStyle === 'compact') {
        // Compact button for the actions column - retaining text for better UX
        return (
            <button 
                className="add-node-button-compact"
                onClick={handleShowAddForm}
                disabled={availableTiers.length === 0}
                title={availableTiers.length === 0 
                    ? "All warehouse handling tiers already exist for this production capability"
                    : "Add a warehouse handling tier"}
            >
                <span className="add-icon">+</span> Add Warehouse Handling Tier
            </button>
        );
    }
    
    // Standard button for the add row
    return (
        <button 
            className="add-node-button"
            onClick={handleShowAddForm}
            disabled={availableTiers.length === 0}
            title={availableTiers.length === 0 
                ? "All warehouse handling tiers already exist for this production capability"
                : "Add a warehouse handling tier"}
        >
            <span className="add-icon">+</span> Add Warehouse Handling Tier
        </button>
    );
};
